import React from 'react';
import PropTypes from 'prop-types';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  IconButton, 
  Box, 
  Alert, 
  Button 
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

// Helper function defined outside the component
const getYouTubeEmbedUrl = (url) => {
  // 1. Crash Fix: Ensure url is actually a string
  if (!url || typeof url !== 'string') return null;

  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);

  if (match && match[2].length === 11) {
    // 2. Proxy Fix: Use youtube-nocookie domain
    return `https://www.youtube-nocookie.com/embed/${match[2]}`;
  }
  return null;
};

const VideoPlayerModal = ({ open, onClose, videoUrl, title }) => {
  const embedUrl = getYouTubeEmbedUrl(videoUrl);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="video-modal-title"
    >
      <DialogTitle
        id="video-modal-title"
        sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Box component="span" sx={{ typography: 'h6', flexGrow: 1, mr: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {title || 'Video Player'}
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers sx={{ p: 0, bgcolor: 'black', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
        {embedUrl ? (
          <Box sx={{ width: '100%', aspectRatio: '16 / 9' }}>
            <iframe
              src={embedUrl}
              title={title || "Video"}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{ width: '100%', height: '100%' }}
            />
          </Box>
        ) : (
          <Box sx={{ p: 4, width: '100%' }}>
            <Alert severity="error">
              Invalid video URL or format not supported.
              <br />
              {/* Fallback link if embed fails completely */}
              {videoUrl && typeof videoUrl === 'string' && (
                 <a href={videoUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', marginTop: '8px', display: 'block' }}>
                   Click here to open externally
                 </a>
              )}
            </Alert>
          </Box>
        )}
      </DialogContent>

      {/* 3. UX Fix: Action bar to open externally if proxy blocks playback */}
      <DialogActions sx={{ justifyContent: 'center', p: 1.5 }}>
        <Button 
          startIcon={<OpenInNewIcon />} 
          href={typeof videoUrl === 'string' ? videoUrl : '#'} 
          target="_blank" 
          rel="noopener noreferrer"
          color="primary"
          disabled={!videoUrl || typeof videoUrl !== 'string'}
        >
          Watch on YouTube
        </Button>
      </DialogActions>
    </Dialog>
  );
};

VideoPlayerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  videoUrl: PropTypes.string,
  title: PropTypes.string,
};

export default VideoPlayerModal;